import React, { useState, useRef, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from "react-router-dom"
import { Container, Col, Row, Button, ButtonGroup } from 'react-bootstrap'
import { Stack } from '@mui/material'

import moment from 'moment'
import { saveAs } from 'file-saver'

import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import TimeSeriesPlot from 'Shared/Components/TimeSeriesPlot'
import MapReplay from './components/MapReplay'
import { FloatingDatePicker, FloatingDropdown, FloatingTimeRangePicker } from 'Shared/Components/FloatingLabel/FloatingLabel'
import { smartwatchMetadata } from 'Shared/constant'

const SmartwatchOutdoorHistory = () => {
  const routeParam = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { send } = useFetchData()
  const { setPageLoading } = useOutletContext()

  const mapRef = useRef()

  const [workers, setWorkers] = useState([])
  const [workerSelected, setWorkerSelected] = useState()

  const [data, setData] = useState({})
  const [threshold, setThreshold] = useState({})
  const [referenceLines, setReferenceLines] = useState({})

  const [date, setDate] = useState()
  const [time, setTime] = useState()
  const [plotRange, setPlotRange] = useState({ "start": 0, "end": 24 })

  const fetchMapExtent = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/map_extent`,
      returnType: "json"
    })

    mapRef.current.setMapExtent(body)
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)
      
      await Promise.all([
        onDateChange(new Date()),
        fetchMapExtent()
      ])

      setPageLoading(false)
    }

    init()
  }, [])

  const loadData = async () => {
    const body1 = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/threshold`,
      returnType: "json"
    })

    const body2 = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/history`,
      params: {
        "worker": workerSelected,
        "start_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[0]).format("HH:mm:ss")}`).unix(),
        "end_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[1]).format("HH:mm:ss")}`).unix(),
        "type": "outdoor"
      },
      returnType: "json"
    })

    const th = {}
    const refLines = {}

    if (body2 != null) {
      Object.entries(body1).forEach(([k, v]) => {
        const max = Math.max(...(body2["data"].map(obj => obj[k])))
        const min = Math.min(...(body2["data"].map(obj => obj[k])))

        refLines[k] = [...new Set(v.flatMap(item => item.range).filter(value => value !== null && value !== undefined))]

        v.forEach((i, index) => {
          if (i["alert"] === true) {
            th[`${k}-2-${index}`] = [i["range"][0] ?? Math.min(smartwatchMetadata[k]["plotRange"][0], min), i["range"][1] ?? Math.max(smartwatchMetadata[k]["plotRange"][1], max)]
          }
        })
      })

      setThreshold(th)
      setReferenceLines(refLines)

      setData({
        "data": [
          ...[{...th, ...{"time": moment.unix(0)}}],
          ...body2["data"].map((i) => (
            {...th, ...{
              "time": new Date(moment(i["updated_at"])),
              "body_temp": i["body_temp"],
              "heart_rate": i["heart_rate"]
            }}
          )),
          ...[{...th, ...{"time": moment.unix(2147483648)}}],
        ]
      })

      mapRef.current.updateData(body2["location"]["points"], body2["location"]["lines"])
    }
    else {
      setData()

      mapRef.current.updateData([], [])
    }
  }

  const onDateChange = async (date) => {
    setDate(date)

    setPageLoading(true)

    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/worker`,
      params: {
        "type": "outdoor",
        "date": moment(date).format("YYYY-MM-DD")
      },
      returnType: "json"
    })

    body && setWorkers(body.map((i) => ({
      value: i["worker_id"].toString(),
      label: i["worker_name"],
    })))

    setPageLoading(false)
  }

  const update = async () => {
    if (workerSelected && date && time) {
      setPageLoading(true)

      await loadData()

      const start = parseInt(moment(time[0]).startOf('hour').format('HH'))

      let end = moment(time[1])
      if (end.format("mm") !== "00") { end = end.add(1, "hours").startOf('hours') }
      end = end.format('HH')

      setPlotRange({ "start": start, "end": end !== "00" ? parseInt(end) : 24 })

      setPageLoading(false)
    }
  }

  const onWorkerSelected = (v) => {
    const start = moment(date)
    start.set({hour:0,minute:0,second:0})

    const end = moment(date)
    end.set({hour:23,minute:59,second:59})

    setWorkerSelected(v)
    setTime([new Date(start), new Date(end)])
  }

  const report = async () => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/report`,
      params: {
        "worker": workerSelected,
        "start_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[0]).format("HH:mm:ss")}`).unix(),
        "end_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[1]).format("HH:mm:ss")}`).unix(),
        "type": "outdoor"
      },
      returnType: "blob"
    })

    if (blob != null) {
      saveAs(blob, `export.xlsx`)
    }

    setPageLoading(false)
  }

  return (<>
    <div className="mainContainer"  style={{ borderRadius: "15px", backgroundColor: "white", padding: "20px" }}>
      <Container fluid style={{ height: "100%", overflow: "auto" }}>
        <Row className="mt-1 mb-1">
          <Col xs={12} lg={4}>
            <FloatingDatePicker label="Date" name="date" value={date} onChange={(e) => onDateChange(new Date(e))}/>
          </Col>

          <Col xs={12} lg={4}>
            <FloatingDropdown label="Worker Name" name="name" value={workerSelected} options={workers} onChange={(e) => onWorkerSelected(e)} />
          </Col>

          <Col xs={12} lg={4}>
            <FloatingTimeRangePicker label="Time Range" name="time" value={time} onChange={(d) => setTime([new Date(d[0]), new Date(d[1])]) }/>
          </Col>

          <Col xs={12} className="mt-2">
            <ButtonGroup style={{ width: "100%" }}>
              <Button variant="success" onClick={() => update()}>{t("general_search")}</Button>
              <Button variant="info" onClick={() => report()}>{t("general_export")}</Button>
            </ButtonGroup>
          </Col>
        </Row>

        {data && 
          <Row style={{ marginTop: "20px" }}>
            <Col xs={12} style={{ height: "100%", padding: "10px" }}>
              <Stack spacing={3}>
                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartwatch_bodyTemp")}</b>
                  </div>

                  <div style={{ flexGrow: 1, margin: "5px" }}>
                    <TimeSeriesPlot
                      data={data["data"] ?? []}
                      threshold_key={Object.keys(threshold).filter((i) => i.split("-")[0] === "body_temp")}
                      items={[
                        { "axis": "left", "dataKey":"body_temp", "name": t("smartwatch_bodyTemp"), "stroke": "#800080" },
                      ]}
                      day={moment(date).format("YYYY-MM-DD")}
                      hour={[plotRange["start"], plotRange["end"]]}
                      yLeft={{ "range": smartwatchMetadata["body_temp"]["plotRange"], "label": "" }}
                      referenceLines={referenceLines["body_temp"]}
                    />
                  </div>
                </div>

                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartwatch_heartRate")}</b>
                  </div>
                  
                  <div style={{ flexGrow: 1, margin: "5px" }}>
                    <TimeSeriesPlot
                      data={data["data"] ?? []}
                      threshold_key={Object.keys(threshold).filter((i) => i.split("-")[0] === "heart_rate")}
                      items={[
                        { "axis": "left", "dataKey":"heart_rate", "name": t("smartwatch_heartRate"), "stroke": "#800080" },
                      ]}
                      day={moment(date).format("YYYY-MM-DD")}
                      hour={[plotRange["start"], plotRange["end"]]}
                      yLeft={{ "range": smartwatchMetadata["heart_rate"]["plotRange"], "label": "" }}
                      referenceLines={referenceLines["heart_rate"]}
                    />
                  </div>
                </div>

                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartwatch_location")}</b>
                  </div>
                  
                  <div style={{ flexGrow: 1, margin: "5px" }}>
                    <MapReplay ref={mapRef}/>
                  </div>
                </div>
              </Stack>
            </Col>
          </Row>
        }
      </Container> 
    </div>
  </>)
}

export default SmartwatchOutdoorHistory
